/**
 * V2 dependent-exclusion Vuex store module.
 *
 * Mirrors the structure of
 * src/store/ticket/dependants-incorporation/individualIncorporation/individualIncorporation.js
 * and follows the same conventions:
 *  - private client is responsible for auth + base URL
 *  - actions accept plain JS payloads, return response data
 *  - 400/404/409 errors are surfaced with the HTTP status in the
 *    message so the UI can react appropriately
 *
 * Status codes (see CONTEXT.md "Ticket States"):
 *  - 8 = UNDER_REVIEW_INSURANCE_COMPANY (send to insurance)
 *  - 3 = EXECUTED                     (finish)
 *  - 4 = CANCELED                     (cancel)
 */

import { dependentExclusionService } from '../../../../network';

import {
  POST_V2_DEPENDENTS_EXCLUSION,
  FETCH_V2_DEPENDENTS_EXCLUSION_OF_TICKET,
  FETCH_V2_EMPLOYEE_ACTIVE_COVERAGES_WITH_DEPENDENTS,
  POST_V2_SEND_TO_INSURANCE_DEPENDENTS_EXCLUSION,
  POST_V2_FINISH_DEPENDENTS_EXCLUSION,
  POST_V2_CANCEL_DEPENDENTS_EXCLUSION
} from './types';

// Ticket states from CONTEXT.md
const STATE_UNDER_REVIEW_INSURANCE_COMPANY = 8;
const STATE_EXECUTED = 3;
const STATE_CANCELED = 4;

const STATUS_ERROR_TAGS = new Set([400, 404, 409]);

const tagError = (error) => {
  const status = error?.response?.status;
  if (status && STATUS_ERROR_TAGS.has(status)) {
    const backendMessage = error?.response?.data?.message;
    const message = backendMessage
      ? `Dependent exclusion V2 request failed with HTTP ${status}: ${backendMessage}`
      : `Dependent exclusion V2 request failed with HTTP ${status}`;
    const wrapped = new Error(message, { cause: error });
    wrapped.status = status;
    return wrapped;
  }
  return error;
};

const store = {};
const getDefaultState = () => ({});

store.state = getDefaultState();
store.getters = {};
store.mutations = {};

store.actions = {
  async [POST_V2_DEPENDENTS_EXCLUSION](_, { payload, token } = {}) {
    try {
      return await dependentExclusionService.createDependentsExclusion(
        payload,
        token
      );
    } catch (error) {
      throw tagError(error);
    }
  },

  async [FETCH_V2_DEPENDENTS_EXCLUSION_OF_TICKET](_, { ticketId, token } = {}) {
    try {
      return await dependentExclusionService.getTicketDependentExclusions(
        ticketId,
        token
      );
    } catch (error) {
      throw tagError(error);
    }
  },

  async [FETCH_V2_EMPLOYEE_ACTIVE_COVERAGES_WITH_DEPENDENTS](
    _,
    { employeeId, token } = {}
  ) {
    try {
      return await dependentExclusionService.getEmployeeActiveCoveragesWithDependents(
        employeeId,
        token
      );
    } catch (error) {
      throw tagError(error);
    }
  },

  async [POST_V2_SEND_TO_INSURANCE_DEPENDENTS_EXCLUSION](
    _,
    { ticketId, token } = {}
  ) {
    try {
      return await dependentExclusionService.patchTicketStatus(
        ticketId,
        { status: STATE_UNDER_REVIEW_INSURANCE_COMPANY },
        token
      );
    } catch (error) {
      throw tagError(error);
    }
  },

  async [POST_V2_FINISH_DEPENDENTS_EXCLUSION](_, { ticketId, token } = {}) {
    try {
      return await dependentExclusionService.patchTicketStatus(
        ticketId,
        { status: STATE_EXECUTED },
        token
      );
    } catch (error) {
      throw tagError(error);
    }
  },

  async [POST_V2_CANCEL_DEPENDENTS_EXCLUSION](_, { ticketId, token } = {}) {
    try {
      return await dependentExclusionService.patchTicketStatus(
        ticketId,
        { status: STATE_CANCELED },
        token
      );
    } catch (error) {
      throw tagError(error);
    }
  }
};

export default store;
